// Modal functionality

// Greek Letters Data Structure
const GREEK_LETTERS = [
  { symbol: '', name: 'None' },
  { symbol: 'Α', name: 'Alpha' },
  { symbol: 'Β', name: 'Beta' },
  { symbol: 'Γ', name: 'Gamma' },
  { symbol: 'Δ', name: 'Delta' },
  { symbol: 'Ε', name: 'Epsilon' },
  { symbol: 'Ζ', name: 'Zeta' },
  { symbol: 'Η', name: 'Eta' },
  { symbol: 'Θ', name: 'Theta' },
  { symbol: 'Ι', name: 'Iota' },
  { symbol: 'Κ', name: 'Kappa' },
  { symbol: 'Λ', name: 'Lambda' },
  { symbol: 'Μ', name: 'Mu' },
  { symbol: 'Ν', name: 'Nu' },
  { symbol: 'Ξ', name: 'Xi' },
  { symbol: 'Ο', name: 'Omicron' },
  { symbol: 'Π', name: 'Pi' },
  { symbol: 'Ρ', name: 'Rho' },
  { symbol: 'Σ', name: 'Sigma' },
  { symbol: 'Τ', name: 'Tau' },
  { symbol: 'Υ', name: 'Upsilon' },
  { symbol: 'Φ', name: 'Phi' },
  { symbol: 'Χ', name: 'Chi' },
  { symbol: 'Ψ', name: 'Psi' },
  { symbol: 'Ω', name: 'Omega' }
];

class MonogramCustomizer extends HTMLElement {
  constructor() {
    super();
    this.productId = this.getAttribute('data-product-id');
    // elements
    this.openButton = this.querySelector('.monogram-customization__trigger');
    this.closeButton = this.querySelectorAll('.monogram-modal__close');
    this.modal = this.querySelector('.monogram-modal');

    this.previewText = this.querySelector('#monogram-preview-text');
    this.styleOptions = this.querySelectorAll('.monogram-style-option');
    this.textInput = this.querySelector('#MonogramText');
    this.characterLimit = this.querySelector('#monogram-character-limit');
    this.charactersRemaining = this.querySelector('#characters-remaining');
    this.charactersEntered = this.querySelector('#characters-entered');
    this.limitText = this.querySelector('#limit-text');
    this.colorOptions = this.querySelectorAll('.monogram-color-option');
    this.termsCheckbox = this.querySelector('#terms-checkbox');
    this.addToCartButton = this.querySelector('#monogram-add-to-cart');
    this.productInfo = this.closest('product-info');
    this.productFormElement = this.productInfo?.querySelector('product-form');
    this.productSubmitButton = this.productInfo.querySelector('.product-form__submit');
    this.errorMessage = this.querySelector('.monogram-error-message');

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.sectionId === this.productInfo?.dataset?.section) {
          this.productFormElement?.clearMonogramProperties?.();
        }
      });
    }

    if (!this.modal) return;
    //open modal
    this.openButton.addEventListener('click', () => this.handleModalOpen());
    //close modal
    this.closeButton.forEach((button) => {
      button.addEventListener('click', () => this.closeModal());
    });
    // Close when clicking outside the modal content
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // State
    this.selectedStyle = this.styleOptions.length > 0 ? this.styleOptions[0] : null;
    this.selectedColor = this.colorOptions.length > 0 ? this.colorOptions[0] : null;
    this.monogramText = '';
    this.termsAccepted = false;

    // Greek font specific state
    this.greekLetterSelections = []; // Array to store selected Greek letters
    this.isGreekStyle = false;
    this.greekDropdowns = [];
    this.greekCharacterLimit = this.querySelector('#monogram-greek-character-limit');
    this.greekCharactersRemaining = this.querySelector('#greek-characters-remaining');
    this.greekCharactersEntered = this.querySelector('#greek-characters-entered');
    this.greekLimitText = this.querySelector('#greek-limit-text');

    // Initialize
    this.initializeMonogram();
  }

  disconnectedCallback() {
    this.variantChangeUnsubscriber?.();
  }

  // New method to handle modal opening
  handleModalOpen() {
    if (this.productSubmitButton && this.productSubmitButton.hasAttribute('disabled')) {
      // Show error message
      if (this.errorMessage) {
        this.errorMessage.textContent = 'Select a size';

        // Clear error message after 3 seconds
        setTimeout(() => {
          this.errorMessage.textContent = '';
        }, 3000);
      }
    } else {
      // If submit button is not disabled, open the modal
      this.openModal();
    }
  }

  initializeMonogram() {
    // Set initial preview position
    if (this.previewText) {
      this.positionPreviewText();
    }

    // Initialize monogramText from the input field if it has a value
    if (this.textInput && this.textInput.value) {
      this.monogramText = this.textInput.value;
    }

    // Set initial font on the input field
    if (this.textInput && this.selectedStyle) {
      const fontHandle = this.selectedStyle.dataset.fontHandle;
      if (fontHandle) {
        this.textInput.style.fontFamily = `'${fontHandle}'`;
      }
    }

    // Check if initial style is Greek and set up accordingly
    if (this.selectedStyle) {
      this.isGreekStyle = this.isGreekFontStyle(this.selectedStyle);
      if (this.isGreekStyle) {
        const fontType = this.selectedStyle.dataset.fontType;
        const characterLimit = this.selectedStyle.dataset.characterLimit;
        this.switchInputMethod(true, fontType, characterLimit);
      }
    }

    // Add event listener for closing dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.greek-letter-dropdown')) {
        this.closeAllDropdowns();
      }
    });

    // Event listeners
    if (this.styleOptions.length > 0) {
      this.styleOptions.forEach((option) => {
        option.addEventListener('click', this.handleStyleSelect.bind(this));
      });
    }

    if (this.textInput) {
      this.textInput.addEventListener('input', this.handleTextInput.bind(this));
    }

    if (this.colorOptions.length > 0) {
      this.colorOptions.forEach((option) => {
        option.addEventListener('click', this.handleColorSelect.bind(this));
      });
    }

    if (this.termsCheckbox) {
      this.termsCheckbox.addEventListener('change', this.handleTermsChange.bind(this));
    }

    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', this.handleAddToCart.bind(this));
    }

    // Initialize preview
    this.updatePreview();

    // Initialize character counts to ensure mobile and desktop are synced
    this.updateCharacterCount();
  }

  // Greek font detection method
  isGreekFontStyle(styleElement) {
    return styleElement.dataset.isGreek === 'true';
  }

  // Input method switching
  switchInputMethod(isGreek, fontType, characterLimit) {
    const textInputContainer = this.querySelector('.monogram-text-input').parentElement;
    const greekContainer = this.querySelector('.monogram-greek-selection');

    if (isGreek) {
      textInputContainer.style.display = 'none';
      greekContainer.style.display = 'block';
      this.generateGreekDropdowns(fontType, characterLimit);
    } else {
      textInputContainer.style.display = 'block';
      greekContainer.style.display = 'none';
      this.clearGreekDropdowns();
    }
  }

  // Generate Greek dropdowns based on character limit
  generateGreekDropdowns(fontType, characterLimit) {
    const container = this.querySelector('.monogram-greek-dropdowns');
    container.innerHTML = ''; // Clear existing dropdowns
    this.greekDropdowns = [];
    this.greekLetterSelections = [];

    const dropdownCount = parseInt(characterLimit);

    for (let i = 0; i < dropdownCount; i++) {
      const dropdown = this.createGreekDropdown(i);
      container.appendChild(dropdown);
      this.greekDropdowns.push(dropdown);
      this.greekLetterSelections.push(''); // Initialize as empty
    }

    // Update Greek character limit display
    if (this.greekCharacterLimit) {
      this.greekCharacterLimit.textContent = characterLimit;
    }
    // Update desktop (remaining count)
    if (this.greekCharactersRemaining) {
      this.greekCharactersRemaining.textContent = characterLimit;
    }
    // Update mobile (entered count - starts at 0)
    if (this.greekCharactersEntered) {
      this.greekCharactersEntered.textContent = 0;
    }
    // Update limit text (mobile)
    if (this.greekLimitText) {
      this.greekLimitText.textContent = `/ ${characterLimit}`;
    }
  }

  // Create individual Greek dropdown
  createGreekDropdown(index) {
    const dropdown = document.createElement('div');
    dropdown.className = 'greek-letter-dropdown';
    dropdown.dataset.index = index;

    dropdown.innerHTML = `
      <div class="greek-dropdown-trigger">
        <span class="selected-letter"></span>
        <span class="dropdown-arrow">
        <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.7891 0.439209L5.66724 5.56103L0.545426 0.439209" stroke="black" stroke-width="0.818182" stroke-miterlimit="3.075" stroke-linecap="round"/>
        </svg>
        </span>
      </div>
      <div class="greek-dropdown-content">
        ${GREEK_LETTERS.map(
          (letter) => `
          <div class="greek-letter-option" data-symbol="${letter.symbol}">
          <span class="greek-letter-name">${letter.name || 'None'}</span>
          ${letter.symbol ? `<span class="greek-symbol">(${letter.symbol})</span>` : ''}
          </div>
        `,
        ).join('')}
      </div>
    `;

    this.attachDropdownEvents(dropdown, index);
    return dropdown;
  }

  // Attach events to dropdown
  attachDropdownEvents(dropdown, index) {
    const trigger = dropdown.querySelector('.greek-dropdown-trigger');
    const content = dropdown.querySelector('.greek-dropdown-content');
    const options = dropdown.querySelectorAll('.greek-letter-option');

    // Toggle dropdown
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();

      // Check if this dropdown is currently open
      const isCurrentlyOpen = content.classList.contains('show');

      // Close all dropdowns first
      this.closeAllDropdowns();

      // If this dropdown was closed, open it
      if (!isCurrentlyOpen) {
        content.classList.add('show');
        trigger.classList.add('active');
      }
    });

    // Handle letter selection
    options.forEach((option) => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        const symbol = option.dataset.symbol;
        this.selectGreekLetter(index, symbol, dropdown);
      });
    });
  }

  // Handle Greek letter selection
  selectGreekLetter(index, symbol, dropdown) {
    // Update selection array
    this.greekLetterSelections[index] = symbol;

    // Find the letter name from GREEK_LETTERS array
    const letterData = GREEK_LETTERS.find((letter) => letter.symbol === symbol);
    const letterName = letterData ? letterData.name : '';

    // Update UI
    const trigger = dropdown.querySelector('.greek-dropdown-trigger');
    const selectedSpan = trigger.querySelector('.selected-letter');

    // Only show parentheses if there’s a real symbol
    if (!symbol) {
      selectedSpan.textContent = 'NONE';
    } else {
      selectedSpan.textContent = `${letterName.toUpperCase()} (${symbol})`;
    }

    // Close dropdown
    const content = dropdown.querySelector('.greek-dropdown-content');
    content.classList.remove('show');
    trigger.classList.remove('active');

    // Update monogramText for compatibility
    this.monogramText = this.greekLetterSelections.join('');

    // Update character count
    this.updateCharacterCount();

    // Update preview
    this.updatePreview();

    // Check add to cart availability
    this.checkAddToCartAvailability();
  }

  // Close all dropdowns
  closeAllDropdowns() {
    this.greekDropdowns.forEach((dropdown) => {
      const content = dropdown.querySelector('.greek-dropdown-content');
      const trigger = dropdown.querySelector('.greek-dropdown-trigger');
      content.classList.remove('show');
      trigger.classList.remove('active');
    });
  }

  // Clear Greek dropdowns
  clearGreekDropdowns() {
    const container = this.querySelector('.monogram-greek-dropdowns');
    if (container) {
      container.innerHTML = '';
    }
    this.greekDropdowns = [];
    this.greekLetterSelections = [];
  }

  // Update character count for both input types
  updateCharacterCount() {
    if (this.isGreekStyle) {
      // For Greek: count selected letters
      const usedCount = this.greekLetterSelections.filter((letter) => letter !== '').length;
      const limit = parseInt(this.selectedStyle.dataset.characterLimit);

      // Update desktop (remaining count)
      if (this.greekCharactersRemaining) {
        this.greekCharactersRemaining.textContent = limit - usedCount;
      }

      // Update mobile (entered count)
      if (this.greekCharactersEntered) {
        this.greekCharactersEntered.textContent = usedCount;
      }
    } else {
      // For standard: use existing logic
      if (this.selectedStyle) {
        const limit = parseInt(this.selectedStyle.dataset.characterLimit);
        const enteredCount = this.monogramText.length;

        // Update desktop (remaining count)
        if (this.charactersRemaining) {
          this.charactersRemaining.textContent = limit - enteredCount;
        }

        // Update mobile (entered count)
        if (this.charactersEntered) {
          this.charactersEntered.textContent = enteredCount;
        }
      }
    }
  }

  handleStyleSelect(event) {
    // Remove selected class from all options
    this.styleOptions.forEach((option) => {
      option.classList.remove('monogram-style-option--selected');
    });

    // Add selected class to clicked option
    event.currentTarget.classList.add('monogram-style-option--selected');
    this.selectedStyle = event.currentTarget;

    // Check if this is a Greek style and switch input method
    this.isGreekStyle = this.isGreekFontStyle(this.selectedStyle);
    const fontType = this.selectedStyle.dataset.fontType;
    const characterLimit = this.selectedStyle.dataset.characterLimit;

    this.switchInputMethod(this.isGreekStyle, fontType, characterLimit);

    // Sync monogramText with current input value (for non-Greek styles)
    if (this.textInput && !this.isGreekStyle) {
      this.monogramText = this.textInput.value;
    } else if (this.isGreekStyle) {
      // Reset monogram text for Greek styles
      this.monogramText = '';
    }

    // Update character limit and placeholder
    const limit = this.selectedStyle.dataset.characterLimit;
    if (this.characterLimit) {
      this.characterLimit.textContent = limit;
    }

    if (this.textInput) {
      this.textInput.maxLength = limit;
      this.textInput.placeholder = this.selectedStyle.dataset.placeholderText;

      // If current text is longer than new limit, truncate it
      if (this.textInput.value.length > limit) {
        this.textInput.value = this.textInput.value.substring(0, limit);
        // Update monogramText after truncation
        this.monogramText = this.textInput.value;
      }

      // Update characters remaining (desktop)
      if (this.charactersRemaining) {
        this.charactersRemaining.textContent = limit - this.textInput.value.length;
      }

      // Update characters entered (mobile)
      if (this.charactersEntered) {
        this.charactersEntered.textContent = this.textInput.value.length;
      }

      // Update limit text (mobile)
      if (this.limitText) {
        this.limitText.textContent = `/ ${limit}`;
      }

      // Apply the selected font to the input field based on font type
      const fontType = this.selectedStyle.dataset.fontType;

      // Use getAttribute for more reliable access to font data
      const baseFontHandle = this.selectedStyle.getAttribute('data-base-font-handle');

      if (baseFontHandle) {
        this.textInput.style.fontFamily = `'${baseFontHandle}', sans-serif`;
      }
    }

    // Update preview
    this.updatePreview();

    // Update character count and check add to cart availability
    this.updateCharacterCount();
    this.checkAddToCartAvailability();
  }

  handleTextInput(event) {
    // Only handle text input for non-Greek styles
    if (!this.isGreekStyle) {
      this.monogramText = event.target.value;

      // Update characters remaining
      this.updateCharacterCount();

      // Update preview
      this.updatePreview();

      // Check if add to cart should be enabled
      this.checkAddToCartAvailability();
    }
  }

  handleColorSelect(event) {
    // Remove selected class from all options
    this.colorOptions.forEach((option) => {
      option.classList.remove('monogram-color-option--selected');
    });

    // Add selected class to clicked option
    event.currentTarget.classList.add('monogram-color-option--selected');
    this.selectedColor = event.currentTarget;

    // Make sure monogramText is synced with the correct input method
    if (this.isGreekStyle) {
      // For Greek styles, maintain the existing Greek letter selections
      this.monogramText = this.greekLetterSelections.join('');
    } else if (this.textInput) {
      // For standard styles, sync with text input
      this.monogramText = this.textInput.value;
    }

    // Update preview
    this.updatePreview();
  }

  handleTermsChange(event) {
    this.termsAccepted = event.target.checked;
    this.checkAddToCartAvailability();
  }

  checkAddToCartAvailability() {
    if (this.addToCartButton) {
      let isTextValid = false;
      let isMinimumCharactersMet = false;

      if (this.isGreekStyle) {
        // For Greek: check if minimum characters requirement is met
        const minimumCharacters = this.selectedStyle ? parseInt(this.selectedStyle.dataset.minimumCharacters) || 1 : 1;
        const usedCount = this.greekLetterSelections.filter((letter) => letter !== '').length;
        isTextValid = usedCount > 0;
        isMinimumCharactersMet = usedCount >= minimumCharacters;
      } else {
        // For standard: check if minimum characters requirement is met
        const minimumCharacters = this.selectedStyle ? parseInt(this.selectedStyle.dataset.minimumCharacters) || 1 : 1;
        const textLength = this.monogramText.trim().length;
        isTextValid = textLength > 0;
        isMinimumCharactersMet = textLength >= minimumCharacters;
      }

      this.addToCartButton.disabled = !(isTextValid && this.termsAccepted && isMinimumCharactersMet);
    }
  }

  updatePreview() {
    if (!this.previewText) return;

    // Make sure monogramText is synced with the input value (for non-Greek styles)
    if (this.textInput && !this.isGreekStyle && this.textInput.value !== this.monogramText) {
      this.monogramText = this.textInput.value;
    }

    // Get character limit from selected style
    const characterLimit = this.selectedStyle ? parseInt(this.selectedStyle.dataset.characterLimit) : 0;

    // Apply character limit to preview text
    const truncatedText = characterLimit > 0 ? this.monogramText.substring(0, characterLimit) : this.monogramText;

    // Clear the preview text before adding new content
    this.previewText.innerHTML = '';

    // Get the font type
    const fontType = this.selectedStyle ? this.selectedStyle.dataset.fontType : 'standard';

    if (fontType === '3_letter' && truncatedText.length > 0) {
      // Handle 3_letter font type - each of first 3 letters has different font
      for (let i = 0; i < truncatedText.length; i++) {
        const letterSpan = document.createElement('span');
        letterSpan.textContent = truncatedText[i];

        // Get font data using getAttribute for more reliable access
        const letter1FontHandle = this.selectedStyle.getAttribute('data-letter1-font-handle');
        const letter1FontSize = this.selectedStyle.getAttribute('data-letter1-font-size');
        const letter2FontHandle = this.selectedStyle.getAttribute('data-letter2-font-handle');
        const letter2FontSize = this.selectedStyle.getAttribute('data-letter2-font-size');
        const letter3FontHandle = this.selectedStyle.getAttribute('data-letter3-font-handle');
        const letter3FontSize = this.selectedStyle.getAttribute('data-letter3-font-size');
        const baseFontHandle = this.selectedStyle.getAttribute('data-base-font-handle');
        const baseFontSize = this.selectedStyle.getAttribute('data-base-font-size');

        if (i === 0 && letter1FontHandle) {
          letterSpan.style.fontFamily = `'${letter1FontHandle}'`;
          letterSpan.style.fontSize = `${letter1FontSize}px`;
        } else if (i === 1 && letter2FontHandle) {
          letterSpan.style.fontFamily = `'${letter2FontHandle}'`;
          letterSpan.style.fontSize = `${letter2FontSize}px`;
        } else if (i === 2 && letter3FontHandle) {
          letterSpan.style.fontFamily = `'${letter3FontHandle}'`;
          letterSpan.style.fontSize = `${letter3FontSize}px`;
        } else if (baseFontHandle) {
          // For any letter beyond the first 3, use the base font
          letterSpan.style.fontFamily = `'${baseFontHandle}'`;
          letterSpan.style.fontSize = `${baseFontSize}px`;
        }

        // Apply font weight based on style type
        letterSpan.style.fontWeight = this.isGreekStyle ? '600' : '400';

        this.previewText.appendChild(letterSpan);
      }
    } else if (fontType === '2_letter' && truncatedText.length > 0) {
      // Handle 2_letter font type - each of first 2 letters has different font
      for (let i = 0; i < truncatedText.length; i++) {
        const letterSpan = document.createElement('span');
        letterSpan.textContent = truncatedText[i];

        // Get font data using getAttribute for more reliable access
        const letter1FontHandle = this.selectedStyle.getAttribute('data-letter1-font-handle');
        const letter1FontSize = this.selectedStyle.getAttribute('data-letter1-font-size');
        const letter2FontHandle = this.selectedStyle.getAttribute('data-letter2-font-handle');
        const letter2FontSize = this.selectedStyle.getAttribute('data-letter2-font-size');
        const baseFontHandle = this.selectedStyle.getAttribute('data-base-font-handle');
        const baseFontSize = this.selectedStyle.getAttribute('data-base-font-size');

        if (i === 0 && letter1FontHandle) {
          letterSpan.style.fontFamily = `'${letter1FontHandle}', sans-serif`;
          letterSpan.style.fontSize = `${letter1FontSize}px`;
        } else if (i === 1 && letter2FontHandle) {
          letterSpan.style.fontFamily = `'${letter2FontHandle}', sans-serif`;
          letterSpan.style.fontSize = `${letter2FontSize}px`;
        } else if (baseFontHandle) {
          // For any letter beyond the first 2, use the base font
          letterSpan.style.fontFamily = `'${baseFontHandle}', sans-serif`;
          letterSpan.style.fontSize = `${baseFontSize}px`;
        }

        // Apply font weight based on style type
        letterSpan.style.fontWeight = this.isGreekStyle ? '600' : '400';

        this.previewText.appendChild(letterSpan);
      }
    } else {
      // Handle standard font type - all letters use the same font
      // Set the text
      this.previewText.textContent = truncatedText;

      // Apply font from the selected style
      if (this.selectedStyle) {
        // Use getAttribute for more reliable access
        const baseFontHandle = this.selectedStyle.getAttribute('data-base-font-handle');
        const baseFontSize = this.selectedStyle.getAttribute('data-base-font-size');

        if (baseFontHandle) {
          // Apply the font to the preview text
          this.previewText.style.fontFamily = `'${baseFontHandle}', sans-serif`;
          this.previewText.style.fontSize = `${baseFontSize}px`;

          // Also apply the font to the input field
          if (this.textInput) {
            this.textInput.style.fontFamily = `'${baseFontHandle}', sans-serif`;
          }
        }
      }
    }

    // Apply font weight based on style type
    this.previewText.style.fontWeight = this.isGreekStyle ? '600' : '400';

    // Set the color from the selected color
    if (this.selectedColor) {
      this.previewText.style.color = this.selectedColor.dataset.colorValue;
    }

    // Update text positioning based on selected style
    this.positionPreviewText();
  }

  positionPreviewText() {
    if (!this.previewText) return;

    // Set default positioning
    this.previewText.style.position = 'absolute';

    // Check if selected style has positioning values
    if (this.selectedStyle) {
      // Get text rotation value
      const textRotate = this.selectedStyle.getAttribute('data-text-rotate') || '0';
      const rotationValue = parseFloat(textRotate);

      // Set position based on x and y coordinates if available
      if (this.selectedStyle.dataset.xPosition && this.selectedStyle.dataset.yPosition) {
        this.previewText.style.top = `${this.selectedStyle.dataset.yPosition}%`;
        this.previewText.style.left = `${this.selectedStyle.dataset.xPosition}%`;

        // Apply rotation along with translation
        if (rotationValue !== 0) {
          this.previewText.style.transform = `translate(-50%, -50%) rotate(${rotationValue}deg)`;
        } else {
          this.previewText.style.transform = 'translate(-50%, -50%)';
        }
      } else {
        // Use default centered positioning as fallback
        this.previewText.style.top = '50%';
        this.previewText.style.left = '50%';

        // Apply rotation along with translation
        if (rotationValue !== 0) {
          this.previewText.style.transform = `translate(-50%, -50%) rotate(${rotationValue}deg)`;
        } else {
          this.previewText.style.transform = 'translate(-50%, -50%)';
        }
      }

      // Set width if available
      if (this.selectedStyle.dataset.width) {
        this.previewText.style.width = `${this.selectedStyle.dataset.width}%`;
      }

      // Set height if available
      if (this.selectedStyle.dataset.height) {
        this.previewText.style.height = `${this.selectedStyle.dataset.height}%`;
      }
    } else {
      // Use default centered positioning as fallback (no rotation)
      this.previewText.style.top = '50%';
      this.previewText.style.left = '50%';
      this.previewText.style.transform = 'translate(-50%, -50%)';
    }
  }

  /**
   * Generate canvas image with background and monogram text
   * Returns a data URL (base64) of the canvas image
   */
  async generateCanvasImage() {
    return new Promise((resolve, reject) => {
      // Get preview container for dimensions and background image
      const previewContainer = this.querySelector('#monogram-preview-image');
      if (!previewContainer) {
        reject(new Error('Preview container not found'));
        return;
      }

      // Get computed style - needed for background positioning and size
      const computedStyle = window.getComputedStyle(previewContainer);

      // Try to get image URL from img tag first, then fall back to CSS background-image
      let imageUrl = null;
      const imgTag = previewContainer.querySelector('img');

      if (imgTag && imgTag.src) {
        imageUrl = imgTag.src;
      } else {
        // Get background image URL from computed style
        const backgroundImage = computedStyle.backgroundImage;

        // Extract URL from background-image style (handles url("...") or url(...))
        if (backgroundImage && backgroundImage !== 'none') {
          const match = backgroundImage.match(/url\(["']?([^"']+)["']?\)/);
          if (match) {
            imageUrl = match[1];
          }
        }
      }

      if (!imageUrl) {
        reject(new Error('Background image not found'));
        return;
      }

      // Canvas dimensions - use aspect ratio 394:492
      const canvasWidth = 394;
      const canvasHeight = 492;

      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');

      // Load background image
      const bgImage = new Image();
      bgImage.crossOrigin = 'anonymous'; // Handle CORS if needed

      bgImage.onload = () => {
        try {
          // Get background position and size from computed style
          const bgPosition = computedStyle.backgroundPosition || '0% 0%';
          const bgSize = computedStyle.backgroundSize || '100%';

          // Parse background position (format: "x% y%" or "x y")
          const positionMatch = bgPosition.match(/([\d.]+)%?\s+([\d.]+)%?/);
          const bgXPercent = positionMatch ? parseFloat(positionMatch[1]) : 0;
          const bgYPercent = positionMatch ? parseFloat(positionMatch[2]) : 0;

          // Parse background size (format: "xxx%" or "xxxpx")
          const sizeMatch = bgSize.match(/([\d.]+)%/);
          const bgSizePercent = sizeMatch ? parseFloat(sizeMatch[1]) : 100;

          // Calculate scaled image dimensions
          const scale = bgSizePercent / 100;
          const scaledWidth = bgImage.width * scale;
          const scaledHeight = bgImage.height * scale;

          // Calculate position offset
          const offsetX = (canvasWidth - scaledWidth) * (bgXPercent / 100);
          const offsetY = (canvasHeight - scaledHeight) * (bgYPercent / 100);

          // Draw background image
          ctx.drawImage(bgImage, offsetX, offsetY, scaledWidth, scaledHeight);

          // Draw monogram text
          this.drawMonogramTextOnCanvas(ctx, canvasWidth, canvasHeight);

          // Convert canvas to data URL
          const dataUrl = canvas.toDataURL('image/png', 0.95);
          resolve(dataUrl);
        } catch (error) {
          reject(error);
        }
      };

      bgImage.onerror = () => {
        reject(new Error('Failed to load background image'));
      };

      bgImage.src = imageUrl;
    });
  }

  /**
   * Draw monogram text on canvas with all styling
   */
  drawMonogramTextOnCanvas(ctx, canvasWidth, canvasHeight) {
    if (!this.selectedStyle || !this.selectedColor) return;

    // Get character limit and truncate text
    const characterLimit = parseInt(this.selectedStyle.dataset.characterLimit) || 0;
    const truncatedText = characterLimit > 0 ? this.monogramText.substring(0, characterLimit) : this.monogramText;

    if (!truncatedText || truncatedText.length === 0) return;

    // Get positioning values
    const xPosition = parseFloat(this.selectedStyle.dataset.xPosition) || 50;
    const yPosition = parseFloat(this.selectedStyle.dataset.yPosition) || 50;
    const textRotate = parseFloat(this.selectedStyle.getAttribute('data-text-rotate') || '0');
    const width = parseFloat(this.selectedStyle.dataset.width) || null;
    const height = parseFloat(this.selectedStyle.dataset.height) || null;

    // Get color
    const textColor = this.selectedColor.dataset.colorValue || '#000000';

    // Get font type
    const fontType = this.selectedStyle.dataset.fontType || 'standard';

    // Calculate text position in pixels
    const textX = (canvasWidth * xPosition) / 100;
    const textY = (canvasHeight * yPosition) / 100;

    // Set text alignment
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Save context for rotation
    ctx.save();

    // Translate to text position and rotate
    ctx.translate(textX, textY);
    if (textRotate !== 0) {
      ctx.rotate((textRotate * Math.PI) / 180);
    }

    // Set text color
    ctx.fillStyle = textColor;

    // Set font weight
    const fontWeight = this.isGreekStyle ? '600' : '400';

    if (fontType === '3_letter' && truncatedText.length > 0) {
      // Handle 3_letter font type
      this.drawMultiFontText(ctx, truncatedText, fontType, fontWeight, width, height, canvasWidth, canvasHeight);
    } else if (fontType === '2_letter' && truncatedText.length > 0) {
      // Handle 2_letter font type
      this.drawMultiFontText(ctx, truncatedText, fontType, fontWeight, width, height, canvasWidth, canvasHeight);
    } else {
      // Handle standard font type
      const baseFontHandle = this.selectedStyle.getAttribute('data-base-font-handle');
      const baseFontSize = parseFloat(this.selectedStyle.getAttribute('data-base-font-size')) || 80;

      if (baseFontHandle) {
        ctx.font = `${fontWeight} ${baseFontSize}px '${baseFontHandle}', sans-serif`;
      } else {
        ctx.font = `${fontWeight} ${baseFontSize}px sans-serif`;
      }

      // Handle text wrapping if width is specified
      if (width) {
        const maxWidth = (canvasWidth * width) / 100;
        this.drawWrappedText(ctx, truncatedText, 0, 0, maxWidth, baseFontSize);
      } else {
        ctx.fillText(truncatedText, 0, 0);
      }
    }

    // Restore context
    ctx.restore();
  }

  /**
   * Draw text with multiple fonts (for 2_letter and 3_letter types)
   */
  drawMultiFontText(ctx, text, fontType, fontWeight, width, height, canvasWidth, canvasHeight) {
    const letter1FontHandle = this.selectedStyle.getAttribute('data-letter1-font-handle');
    const letter1FontSize = parseFloat(this.selectedStyle.getAttribute('data-letter1-font-size')) || 80;
    const letter2FontHandle = this.selectedStyle.getAttribute('data-letter2-font-handle');
    const letter2FontSize = parseFloat(this.selectedStyle.getAttribute('data-letter2-font-size')) || 80;
    const letter3FontHandle = this.selectedStyle.getAttribute('data-letter3-font-handle');
    const letter3FontSize = parseFloat(this.selectedStyle.getAttribute('data-letter3-font-size')) || 80;
    const baseFontHandle = this.selectedStyle.getAttribute('data-base-font-handle');
    const baseFontSize = parseFloat(this.selectedStyle.getAttribute('data-base-font-size')) || 80;

    // Calculate total width first to center the text
    let totalWidth = 0;
    const letterWidths = [];
    const letterFonts = [];
    const letterSizes = [];

    for (let i = 0; i < text.length; i++) {
      let fontHandle, fontSize;

      if (i === 0 && letter1FontHandle) {
        fontHandle = letter1FontHandle;
        fontSize = letter1FontSize;
      } else if (i === 1 && letter2FontHandle) {
        fontHandle = letter2FontHandle;
        fontSize = letter2FontSize;
      } else if (i === 2 && fontType === '3_letter' && letter3FontHandle) {
        fontHandle = letter3FontHandle;
        fontSize = letter3FontSize;
      } else if (baseFontHandle) {
        fontHandle = baseFontHandle;
        fontSize = baseFontSize;
      } else {
        fontHandle = 'sans-serif';
        fontSize = 80;
      }

      // Set font temporarily to measure
      ctx.font = `${fontWeight} ${fontSize}px '${fontHandle}', sans-serif`;
      const metrics = ctx.measureText(text[i]);
      const letterWidth = metrics.width;

      letterWidths.push(letterWidth);
      letterFonts.push(fontHandle);
      letterSizes.push(fontSize);
      totalWidth += letterWidth;
    }

    // Start from left edge of total width (centered)
    let currentX = -totalWidth / 2;

    // Draw each letter
    for (let i = 0; i < text.length; i++) {
      ctx.font = `${fontWeight} ${letterSizes[i]}px '${letterFonts[i]}', sans-serif`;
      ctx.fillText(text[i], currentX + letterWidths[i] / 2, 0);
      currentX += letterWidths[i];
    }
  }

  /**
   * Draw text with word wrapping
   */
  drawWrappedText(ctx, text, x, y, maxWidth, fontSize) {
    const words = text.split('');
    let line = '';
    let lineY = y - fontSize * 0.3; // Adjust baseline

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i];
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth && line.length > 0) {
        ctx.fillText(line, x, lineY);
        line = words[i];
        lineY += fontSize * 1.2; // Line height
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x, lineY);
  }

  async handleAddToCart() {
    const productForm = this.productInfo?.querySelector('product-form form');
    const loadingContainer = this.querySelector('.monogram-modal__loading-container');

    if (!productForm || !this.selectedStyle || !this.selectedColor) return;

    // Validate monogram text
    if (!this.monogramText || this.monogramText.trim().length === 0) {
      console.warn('Cannot add to cart: Monogram text is empty');
      return;
    }

    // Show loading indicators
    this.toggleLoading(true);
    if (loadingContainer) {
      loadingContainer.classList.add('active');
    }

    try {
      // Generate image using canvas and get data URL (base64)
      const dataUrl = await this.generateCanvasImage();

      // Extract base64 content (remove the data:image/png;base64, prefix)
      const base64Image = dataUrl.split(',')[1];

      // Send to API to get permanent URL
      const response = await fetch('https://monogram-shopify-api-2bc142f90206.herokuapp.com/api/imagetolink', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          img_to_upload: base64Image,
        }),
      });

      const result = await response.json();

      if (result.status === 'success') {
        const imageUrl = result.data.image_url;

        // Add line item properties
        const properties = [
          { name: 'Select a Style', value: this.selectedStyle.dataset.fontFamily },
          { name: `Enter Text - ${this.selectedStyle.dataset.characterLimit} letters`, value: this.monogramText },
          { name: 'Select Color', value: this.selectedColor.dataset.colorLabel },
          { name: 'I acknowledge that monogrammed items are not eligible for returns or exchanges', value: 'Yes' },
          { name: 'Preview', value: imageUrl }, // Use the permanent image URL instead
        ];

        this.productFormElement?.clearMonogramProperties?.();

        // Add properties to the form
        properties.forEach((prop) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${prop.name}]`;
          input.value = prop.value;
          input.setAttribute('data-monogram-property', '');
          productForm.appendChild(input);
        });

        // Close the modal
        const customizer = document.querySelector('monogram-customizer');
        if (customizer) {
          MonogramCustomizer.closeModalFromOutside(customizer);
        }

        // Submit the form
        const submitButton = productForm.querySelector('[type="submit"]');
        if (submitButton) {
          if (this.productFormElement) {
            this.productFormElement.pendingMonogramSubmit = true;
          }
          submitButton.click();
        }
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error in handleAddToCart:', error);
      // If there's an error, we should provide feedback to the user
      alert('There was an error adding your monogrammed item to cart. Please try again.');
    } finally {
      // Hide loading indicators regardless of success or failure
      this.toggleLoading(false);
      if (loadingContainer) {
        loadingContainer.classList.remove('active');
      }
    }
  }

  toggleLoading(isLoading) {
    if (!this.addToCartButton) return;

    if (isLoading) {
      this.addToCartButton.classList.add('is-loading');
      this.addToCartButton.setAttribute('disabled', 'disabled');
      // Store original text and set loading text
      this.addToCartButton._originalText = this.addToCartButton.textContent;
      this.addToCartButton.innerHTML = '<span class="loading-spinner"></span><span class="loading-text">Adding To Cart</span>';
    } else {
      this.addToCartButton.classList.remove('is-loading');
      // Re-check availability using the same validation logic (includes character limit check)
      this.checkAddToCartAvailability();
      // Restore original text
      if (this.addToCartButton._originalText) {
        this.addToCartButton.textContent = this.addToCartButton._originalText;
      }
    }
  }

  openModal() {
    if (this.modal) {
      document.body.classList.add('overflow-hidden', 'monogram-modal-open');
      this.modal.classList.add('active');
    }
  }

  closeModal() {
    if (this.modal) {
      document.body.classList.remove('overflow-hidden', 'monogram-modal-open');
      this.modal.classList.remove('active');
    }
  }

  // Static method to close from outside the class context
  static closeModalFromOutside(customizer) {
    if (customizer && typeof customizer.closeModal === 'function') {
      customizer.closeModal();
    }
  }
}

customElements.define('monogram-customizer', MonogramCustomizer);
